import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight, Flame } from "lucide-react";
import TrustBadge from "../marketplace/TrustBadge";
import api from "../../lib/api";

/** Small SVG sparkline of daily totals. Auto-fits container. */
function Sparkline({ points, width = 200, height = 40 }) {
  if (!points || points.length < 2) {
    return <div className="text-[10px] font-mono text-ink-faint">Not enough history yet.</div>;
  }
  const totals = points.map((p) => p.total).filter((n) => typeof n === "number");
  if (totals.length < 2) return <div className="text-[10px] font-mono text-ink-faint">Not enough history yet.</div>;
  const min = Math.min(...totals, 0);
  const max = Math.max(...totals, 100);
  const range = Math.max(1, max - min);
  const stepX = width / (totals.length - 1);
  const coords = totals.map((v, i) => [i * stepX, height - ((v - min) / range) * height]);
  const path = coords.map(([x, y], i) => (i === 0 ? `M ${x.toFixed(2)},${y.toFixed(2)}` : `L ${x.toFixed(2)},${y.toFixed(2)}`)).join(" ");
  const last = coords[coords.length - 1];
  const first = coords[0];
  const trending = totals[totals.length - 1] - totals[0];
  const stroke = trending >= 0 ? "#059669" : "#dc2626";
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block" data-testid="trust-sparkline">
      <path d={path} fill="none" stroke={stroke} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={first[0]} cy={first[1]} r={2} fill={stroke} opacity={0.6} />
      <circle cx={last[0]} cy={last[1]} r={2.8} fill={stroke} />
    </svg>
  );
}

/** Founder dashboard trust card. Shows current score, label, sparkline, and passport CTA. */
export default function TrustScoreCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/startup/trust-summary")
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  if (error) return null;
  if (!data) {
    return (
      <div className="border border-white/10 bg-white/70 backdrop-blur p-5 rounded-lg" data-testid="founder-trust-card-loading">
        <div className="overline">VenturaZ Trust Score</div>
        <div className="text-sm text-ink-muted mt-2">Loading…</div>
      </div>
    );
  }

  return (
    <div
      className="border border-gold/40 bg-white/80 backdrop-blur p-5 rounded-lg flex flex-col gap-3"
      data-testid="founder-trust-card"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="overline flex items-center gap-1"><ShieldCheck size={12} /> VENTURAZ TRUST SCORE</div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="font-heading text-3xl text-ink" data-testid="founder-trust-total">
              {data.total ?? "—"}
            </span>
            <span className="text-xs text-ink-muted font-mono">/ 100</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <TrustBadge total={data.total} label={data.label} size="md" testIdPrefix="founder-trust" />
          {(data.streak?.streak_days || 0) > 1 && (
            <div
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 border border-orange-300 text-orange-700 text-[11px] font-mono"
              title={`Score has been non-decreasing for ${data.streak.streak_days} day${data.streak.streak_days === 1 ? "" : "s"}${data.streak.improvement ? `, up ${data.streak.improvement} points` : ""}`}
              data-testid="founder-trust-streak"
            >
              <Flame size={11} />
              {data.streak.streak_weeks >= 1
                ? `${data.streak.streak_weeks}-week streak`
                : `${data.streak.streak_days}-day streak`}
              {data.streak.improvement > 0 ? ` · +${data.streak.improvement}` : ""}
            </div>
          )}
        </div>
      </div>

      <div>
        <Sparkline points={data.history || []} />
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-faint mt-1">
          Last 30 days · {(data.history || []).length} snapshot{(data.history || []).length === 1 ? "" : "s"}
        </div>
      </div>

      <Link
        to="/startup/passport"
        className="text-xs font-mono uppercase tracking-wider text-gold hover:text-gold/80 transition-colors flex items-center gap-1 self-start"
        data-testid="founder-trust-passport-link"
      >
        View Verified Passport <ArrowRight size={12} />
      </Link>
    </div>
  );
}
